import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playTone } from "@/lib/sounds";

function fmt(ms) {
  return (Math.max(0, ms) / 1000).toFixed(2);
}

const SIZE = 300;
const MID = SIZE / 2;
const R_OUT = 138;
const R_ARC = 118;
const C = 2 * Math.PI * R_ARC;
const TICKS = Array.from({ length: 60 }, (_, i) => i);

function Ticks() {
  return TICKS.map((i) => {
    const major = i % 5 === 0;
    const a = (i / 60) * 2 * Math.PI - Math.PI / 2;
    const r1 = R_OUT - (major ? 12 : 6);
    return (
      <line key={i} x1={MID + r1 * Math.cos(a)} y1={MID + r1 * Math.sin(a)}
        x2={MID + R_OUT * Math.cos(a)} y2={MID + R_OUT * Math.sin(a)}
        stroke={major ? "#F5D042" : "rgba(245,208,66,0.28)"} strokeWidth={major ? 2.5 : 1.2} strokeLinecap="round" />
    );
  });
}

const Stopwatch = forwardRef(function Stopwatch({ target, running, onTick }, ref) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);
  const rafRef = useRef(null);
  const runningRef = useRef(false);
  const secRef = useRef(0);
  const [flash, setFlash] = useState(0);

  const loop = () => {
    if (!runningRef.current) return;
    const e = performance.now() - startRef.current;
    const s = Math.floor(e / 1000);
    if (s > secRef.current) { secRef.current = s; playTone("tick"); }
    setElapsed(e);
    if (onTick) onTick(e / 1000);
    rafRef.current = requestAnimationFrame(loop);
  };

  useImperativeHandle(ref, () => ({
    start() {
      setElapsed(0);
      setFlash(0);
      secRef.current = 0;
      startRef.current = performance.now();
      runningRef.current = true;
      rafRef.current = requestAnimationFrame(loop);
    },
    stop() {
      if (!runningRef.current) return elapsed / 1000;
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const finalMs = performance.now() - startRef.current;
      setElapsed(finalMs);
      if (target && (finalMs / 1000).toFixed(2) === Number(target).toFixed(2)) {
        setFlash(Date.now());
        playTone("perfect");
      }
      return finalMs / 1000;
    },
    reset() {
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setElapsed(0);
      setFlash(0);
    },
  }));

  useEffect(() => () => rafRef.current && cancelAnimationFrame(rafRef.current), []);

  const value = fmt(elapsed);
  const progress = target ? Math.min(1, elapsed / 1000 / target) : 0;
  const ang = progress * 2 * Math.PI - Math.PI / 2;
  const sweepDeg = ((elapsed / 1000) % 1) * 360;

  return (
    <div className="relative flex items-center justify-center pointer-events-none select-none"
      style={{ width: SIZE, height: SIZE, maxWidth: "84vw", maxHeight: "84vw" }} data-testid="stopwatch-gauge">
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 h-5 w-14 rounded-t-lg bg-pazer-line border border-pazer-red/40" />
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${SIZE} ${SIZE}`}>
        <circle cx={MID} cy={MID} r={R_OUT + 8} fill="#122266" stroke="#1E358A" strokeWidth="3" />
        <Ticks />
        <circle cx={MID} cy={MID} r={R_ARC} fill="none" stroke="rgba(245,208,66,0.08)" strokeWidth="10" />
        <motion.circle
          cx={MID} cy={MID} r={R_ARC} fill="none"
          stroke="#F5D042" strokeWidth="10" strokeLinecap="round" strokeDasharray={C}
          animate={{
            strokeDashoffset: running ? C * (1 - progress) : C,
            filter: running ? "drop-shadow(0 0 12px rgba(245,208,66,0.8))" : "none",
          }}
          transition={{ duration: 0.08, ease: "linear" }}
          transform={`rotate(-90 ${MID} ${MID})`}
        />
        <circle cx={MID} cy={MID - R_ARC} r="7" fill="#0A174E" stroke="#F5D042" strokeWidth="3" />
        {running && (
          <circle cx={MID + R_ARC * Math.cos(ang)} cy={MID + R_ARC * Math.sin(ang)} r="6" fill="#fff"
            style={{ filter: "drop-shadow(0 0 8px #F5D042)" }} />
        )}
        <circle cx={MID} cy={MID} r={R_ARC - 16} fill="#071038" stroke="#1E358A" strokeWidth="2" />
        <g transform={`rotate(${running ? sweepDeg : 0} ${MID} ${MID})`}>
          <line x1={MID} y1={MID} x2={MID} y2={MID - (R_ARC - 26)} stroke="rgba(245,208,66,0.35)" strokeWidth="2" strokeLinecap="round" />
        </g>
      </svg>
      <AnimatePresence>
        {flash > 0 && (
          <motion.div key={flash} data-testid="stopwatch-perfect-flash"
            initial={{ opacity: 0.9, scale: 0.85 }} animate={{ opacity: 0, scale: 1.35 }} exit={{ opacity: 0 }}
            transition={{ duration: 1.1, ease: "easeOut" }}
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(245,208,66,0.85) 0%, rgba(245,208,66,0.35) 45%, transparent 70%)", boxShadow: "0 0 80px 20px rgba(245,208,66,0.6)" }} />
        )}
      </AnimatePresence>
      {flash > 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} data-testid="stopwatch-perfect-label"
          className="absolute bottom-6 px-3 py-1 rounded-full bg-pazer-red font-display font-extrabold text-xs tracking-widest">PERFECT STOP</motion.div>
      )}
      <div className="relative flex flex-col items-center pointer-events-none">
        <div className="font-mono-pz text-[10px] uppercase tracking-[0.35em] text-white/40 mb-1">Seconds</div>
        <motion.div
          animate={{ scale: running ? [1, 1.02, 1] : 1 }}
          transition={{ duration: 0.8, repeat: running ? Infinity : 0 }}
          className={`font-mono-pz font-black tracking-tighter tabular-nums ${running ? "text-pazer-red pz-glow" : "text-white"}`}
          style={{ fontSize: "clamp(2.8rem, 14vw, 4.5rem)", lineHeight: 1 }}
          data-testid="stopwatch-display"
        >
          {value}
        </motion.div>
        {target ? (
          <div className="mt-3 px-3 py-1 rounded-full border border-pazer-red/40 bg-pazer-card font-mono-pz text-xs font-bold text-white uppercase tracking-widest" data-testid="stopwatch-target">
            Target · <span className="text-pazer-red">{Number(target).toFixed(2)}s</span>
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default Stopwatch;
